'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const quickLinks = [
    { label: 'Home', href: '/home-landing' },
    { label: 'Menu', href: '/menu-catalog' },
    { label: 'About Us', href: '/about-restaurant' },
    { label: 'Gallery', href: '/food-gallery' },
  ];

  const contactInfo = [
    { icon: 'PhoneIcon', text: '+91 75420 72555 | 70506 93691' },
    { icon: 'EnvelopeIcon', text: 'hello@heydgreen.com' },
    { icon: 'MapPinIcon', text: 'Hotel Subheksha, NIT More, Tata Kandra Highway, Adityapur, Jamshedpur - 831013' },
  ];

  const socialLinks = [
    { type: 'meta', label: 'Meta (Facebook)', href: '#' },
    { type: 'instagram', label: 'Instagram', href: '#' },
    { type: 'twitter', label: 'Twitter', href: '#' },
  ];

  return (
    <footer className={`border-t border-border bg-card ${className}`}>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand Section */}
          <div>
            <Link href="/home-landing" className="mb-4 flex items-center space-x-3 focus-ring">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary">
                <Icon name="BuildingStorefrontIcon" size={28} className="text-primary-foreground" />
              </div>
              <span className="font-heading text-xl font-bold text-foreground">Freshlymade</span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Delivering delicious food with passion and care. Fresh ingredients, authentic flavors,
              and fast delivery to your doorstep.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="mb-4 font-heading text-base font-semibold text-foreground">
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-muted-foreground transition-smooth hover:text-primary focus-ring"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="mb-4 font-heading text-base font-semibold text-foreground">
              Contact Us
            </h3>
            <ul className="space-y-3">
              {contactInfo.map((info, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <Icon name={info.icon} size={18} className="mt-0.5 text-primary" />
                  <span className="text-sm text-muted-foreground">{info.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h3 className="mb-4 font-heading text-base font-semibold text-foreground">
              Business Hours
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex justify-between">
                <span>Monday - Friday:</span>
                <span className="text-data font-medium">12:00 PM - 11:00 PM</span>
              </li>
              <li className="flex justify-between">
                <span>Saturday:</span>
                <span className="text-data font-medium">12:00 PM - 10:00 PM</span>
              </li>
              <li className="flex justify-between">
                <span>Sunday:</span>
                <span className="text-data font-medium">12:00 PM - 10:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 border-t border-border pt-6">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row sm:gap-6">
            <p className="text-caption text-sm text-muted-foreground">
              {isHydrated ? `© ${currentYear}` : '© 2026'} Freshlymade. All rights reserved.
            </p>

            <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-sm text-muted-foreground" aria-label="Policy links">
              <Link href="/policies/terms" className="transition-smooth hover:text-primary focus-ring">
                Terms & Conditions
              </Link>
              <Link href="/policies/privacy" className="transition-smooth hover:text-primary focus-ring">
                Privacy
              </Link>
              <Link href="/policies/refund" className="transition-smooth hover:text-primary focus-ring">
                Refund Policy
              </Link>
              <Link href="/policies/shipping" className="transition-smooth hover:text-primary focus-ring">
                Shipping Policy
              </Link>
            </nav>

            <div className="flex items-center space-x-6" aria-label="Social media links">
              {socialLinks.map((social) => (
                <a
                  key={social.type}
                  href={social.href}
                  className="text-muted-foreground transition-smooth hover:text-primary focus-ring"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {social.type === 'meta' && (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  )}
                  {social.type === 'instagram' && (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C8.333.014 7.941 0 7.5 0 12 0zM12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                  )}
                  {social.type === 'twitter' && (
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
